import { ReactElement, useMemo, useCallback, useState, useEffect } from 'react';

import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import UseAnimations from 'react-useanimations';
import loadingIcon from 'react-useanimations/lib/loading';

import { GoBackButton } from '~/components';
import { useLoading } from '~/hooks/loading';
import { TrainingStoreService } from '~/services';
import { light } from '~/styles/themes';

import * as S from './styles';
import { Divisor, TrainingStoreTitle } from './styles';

interface IProfileCode {
  name: string;
  codes: { name: string; code: string }[];
}

export const profileCodes: IProfileCode[] = [
  {
    name: 'SEXO',
    codes: [
      { name: 'S/1', code: 'SX00' },
      { name: 'MASCULINO', code: 'SX01' },
      { name: 'FEMININO', code: 'SX02' },
      { name: 'OUTROS', code: 'SX03' },
    ],
  },
  {
    name: 'NÍVEL TÉCNICO',
    codes: [
      { name: 'S/1', code: 'NT00' },
      { name: 'SEDENTÁRIO', code: 'NT01' },
      { name: 'INICIANTE', code: 'NT02' },
      { name: 'INTERMEDIÁRIO', code: 'NT03' },
      { name: 'AVANÇADO', code: 'NT04' },
      { name: 'ATLETA', code: 'NT05' },
    ],
  },
  {
    name: 'AVALIAÇÃO FÍSICA',
    codes: [
      { name: 'S/1', code: 'AV00' },
      { name: 'BAIXO PESO', code: 'AV01' },
      { name: 'PESO NORMAL', code: 'AV02' },
      { name: 'EXCESSO DE PESO', code: 'AV03' },
      { name: 'OBESIDADE 01', code: 'AV04' },
      { name: 'OBESIDADE 02', code: 'AV05' },
      { name: 'OBESIDADE 03', code: 'AV06' },
    ],
  },
  {
    name: 'OBJETIVO',
    codes: [
      { name: 'S/I', code: 'OT00' },
      { name: 'CONDICIONAMENTO', code: 'OT01' },
      { name: 'HIPERTROFIA', code: 'OT02' },
      { name: 'ESTÉTICA', code: 'OT03' },
      { name: 'EMAGRECIMENTO', code: 'OT04' },
      { name: 'QUALIDADE DE VIDA', code: 'OT05' },
    ],
  },
  {
    name: 'FREQUENCIA',
    codes: [
      { name: 'S/I', code: 'FQ00' },
      { name: '1x', code: 'FQ01' },
      { name: '2x', code: 'FQ02' },
      { name: '3x', code: 'FQ03' },
      { name: '4x', code: 'FQ04' },
      { name: '5x', code: 'FQ05' },
      { name: '6x', code: 'FQ06' },
      { name: '7x', code: 'FQ07' },
    ],
  },
  {
    name: 'TIME AVAIABLE',
    codes: [
      { name: 'S/I', code: 'TA00' },
      { name: '20" - 30"', code: 'TA01' },
      { name: '30"', code: 'TA02' },
      { name: '30" - 45"', code: 'TA03' },
      { name: '45" - 60"', code: 'TA04' },
      { name: '60" - 90"', code: 'TA05' },
      { name: '90"+', code: 'TA06' },
    ],
  },
];

function TrainingStore(): ReactElement {
  const { setLoading } = useLoading();

  const router = useHistory();
  const [trainingStores, setTrainingStores] = useState<string[]>([]);
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

  const handleGoExercisesList = useCallback(
    (exerciseArea: string) => {
      router.push(
        `/ilab/training-store/list/${encodeURIComponent(exerciseArea)}`,
      );
    },
    [router],
  );

  const handleGoProfileCodeList = useCallback(
    (index: number) => {
      router.push(`/ilab/training-store/profile-code-list/${index}`);
    },
    [router],
  );

  const handleGetTrainingStores = useCallback(async () => {
    try {
      setLoading(true);

      const response = await TrainingStoreService.getAllTrainingStores();
      const formatResponse: string[] = [];
      response.data.forEach((item) => {
        if (
          !formatResponse.includes(item.category) &&
          item.category !== 'nivel'
        )
          formatResponse.push(item.category);
      });
      const sortedFormatResponse = formatResponse.sort();

      setTrainingStores(sortedFormatResponse);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error('Ocorreu um problema ao carregar os dados!');
    }
  }, []);

  useEffect(() => {
    handleGetTrainingStores();
  }, [handleGetTrainingStores, modalIsOpen]);

  const TechnicsDataMenuMemo = useMemo(
    () =>
      trainingStores.map((category) => (
        <S.TechnicsDataMenuItem key={category}>
          <S.TechnicsDataMenuLink
            onClick={() => handleGoExercisesList(category)}
          >
            {category} <span>{'>'}</span>
          </S.TechnicsDataMenuLink>
        </S.TechnicsDataMenuItem>
      )),
    [handleGoExercisesList, trainingStores],
  );

  const ProfileCodeMenuMemo = useMemo(
    () =>
      profileCodes.map((item, index) => (
        <S.TechnicsDataMenuItem key={item.name}>
          <S.TechnicsDataMenuLink
            onClick={() => handleGoProfileCodeList(index)}
          >
            {item.name} <span>{'>'}</span>
          </S.TechnicsDataMenuLink>
        </S.TechnicsDataMenuItem>
      )),
    [handleGoProfileCodeList],
  );

  return (
    <>
      <GoBackButton
        containerStyle={{ alignSelf: 'flex-start', paddingTop: '2.2rem' }}
      />
      <S.Container>
        {trainingStores.length > 0 ? (
          <>
            <TrainingStoreTitle>DADOS TÉCNICOS</TrainingStoreTitle>
            <S.TechnicsDataMenu>{TechnicsDataMenuMemo}</S.TechnicsDataMenu>
            <Divisor />
            <TrainingStoreTitle>PROFILE CODE</TrainingStoreTitle>
            <S.TechnicsDataMenu>{ProfileCodeMenuMemo}</S.TechnicsDataMenu>
          </>
        ) : (
          <UseAnimations
            animation={loadingIcon}
            size={56}
            strokeColor={light.colors.yellow}
            wrapperStyle={{
              alignSelf: 'center',
            }}
          />
        )}
      </S.Container>
    </>
  );
}

export default TrainingStore;
